import * as React from 'react';
import { flexRender, useReactTable, type SortingState, type RowData } from 'src/lib/tanstack-react-table';
import noop from 'lodash/noop';
import {
  TableContainer,
  Table as StyledTable,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableLoadingState,
  TableHeadCell,
} from './atoms';
import { ErrorBoundary } from '../ErrorBoundary';
import type { TableProps } from './table-type';
import { CollapsibleRow } from './CollapsibleRow';

/**
 * Table is built on top of @tanstack/react-table v9.
 *
 * Features are registered in `src/lib/tanstack-react-table.ts`
 * (sorting, sizing, visibility). Prefer adding capabilities there before
 * wiring new table behavior ad hoc.
 *
 * @see https://tanstack.com/table/latest for full feature list
 */

export const Table = <TTableData extends RowData>({
  data,
  columns,
  CollapsibleContent,
  showSuccessState = false,
  showLoadingState = false,
  loadingStateRowLength = 5,
  handleRowClick = undefined,
  stickyHeader = false,
}: TableProps<TTableData>) => {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const columnLength = columns?.length || 4;
  const rowLength = loadingStateRowLength || 5;

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    sortDescFirst: true,
  });

  return (
    <TableContainer sx={{ maxHeight: stickyHeader ? 'calc(100vh - 138px)' : '100%' }}>
      <StyledTable stickyHeader={stickyHeader}>
        <ErrorBoundary shouldRetry={false} componentId="Table-TableHead">
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHeadCell key={header.id} headerObj={header} />
                ))}
              </TableRow>
            ))}
          </TableHead>
        </ErrorBoundary>

        <ErrorBoundary shouldRetry={false} componentId="Table-TableBody">
          <TableBody>
            {showSuccessState && !showLoadingState
              ? table.getRowModel().rows.map((row) =>
                  CollapsibleContent ? (
                    <CollapsibleRow
                      row={row}
                      CollapsibleContent={CollapsibleContent}
                      key={row.id}
                      handleRowClick={handleRowClick}
                    />
                  ) : (
                    <TableRow
                      key={row.id}
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      data-id={data?.[Number(row.id)]?.id as string}
                      onClick={handleRowClick || noop}
                      $isBody
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} $size={cell.column.getSize()}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  )
                )
              : null}

            {showLoadingState ? <TableLoadingState columnLength={columnLength} rowLength={rowLength} /> : null}
          </TableBody>
        </ErrorBoundary>
      </StyledTable>
    </TableContainer>
  );
};
